import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface QuickSaleItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export default function QuickSaleSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [items, setItems] = useState<QuickSaleItem[]>([]);
  const [currentPrice, setCurrentPrice] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const priceInputRef = useRef<HTMLInputElement>(null);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  useEffect(() => {
    priceInputRef.current?.focus();
  }, []);

  const handleAddItem = () => {
    const price = parseFloat(currentPrice);
    if (price > 0) {
      const newItem: QuickSaleItem = {
        id: `quick-${Date.now()}`,
        name: currentName || t("posQuickSale.genericItem"),
        price,
        quantity: currentQuantity,
      };
      setItems((prev) => [...prev, newItem]);
      setCurrentPrice("");
      setCurrentName("");
      setCurrentQuantity(1);
      priceInputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddItem();
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleNumpadClick = (value: string) => {
    if (value === "clear") {
      setCurrentPrice("");
    } else if (value === "backspace") {
      setCurrentPrice((prev) => prev.slice(0, -1));
    } else if (value === ".") {
      if (!currentPrice.includes(".")) {
        setCurrentPrice((prev) => prev + value);
      }
    } else {
      setCurrentPrice((prev) => prev + value);
    }
  };

  const subtotal = items.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;

  const quickAmounts = [10, 20, 50, 100, 200, 500];

  return (
    <div className="h-full flex">
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
            <h2 className={`text-xl font-bold ${themeClasses.text} mb-4`}>
              <i className="ri-flashlight-line mr-2 text-primary-500"></i>
              {t("posQuickSale.title")}
            </h2>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posQuickSale.itemName")} ({t("posQuickSale.optional")})
                </label>
                <input
                  type="text"
                  value={currentName}
                  onChange={(e) => setCurrentName(e.target.value)}
                  placeholder={t("posQuickSale.itemNamePlaceholder")}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posQuickSale.price")}
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold ${themeClasses.text}`}>
                    $
                  </span>
                  <input
                    ref={priceInputRef}
                    type="text"
                    inputMode="decimal"
                    value={currentPrice}
                    onChange={(e) => setCurrentPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    onKeyPress={handleKeyPress}
                    placeholder="0.00"
                    className={`w-full pl-10 pr-4 py-4 text-3xl font-bold rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 text-center`}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posQuickSale.quantity")}:
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentQuantity((q) => Math.max(1, q - 1))}
                    className={`w-10 h-10 rounded-lg ${themeClasses.bg} border ${themeClasses.border} flex items-center justify-center`}
                  >
                    <i className={`ri-subtract-line ${themeClasses.text}`}></i>
                  </button>
                  <span className={`font-bold text-xl ${themeClasses.text} min-w-[3rem] text-center`}>
                    {currentQuantity}
                  </span>
                  <button
                    onClick={() => setCurrentQuantity((q) => q + 1)}
                    className="w-10 h-10 rounded-lg bg-primary-500 text-gray-900 flex items-center justify-center"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {quickAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setCurrentPrice(amount.toString())}
                    className={`p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium hover:bg-primary-500 hover:text-gray-900 transition-colors`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-4 gap-2">
                {["7", "8", "9", "backspace"].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleNumpadClick(key)}
                    className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} ${themeClasses.text} font-bold text-xl hover:bg-primary-500 hover:text-gray-900 transition-colors`}
                  >
                    {key === "backspace" ? <i className="ri-delete-back-2-line"></i> : key}
                  </button>
                ))}
                {["4", "5", "6", "clear"].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleNumpadClick(key)}
                    className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} ${key === "clear" ? "text-red-500" : themeClasses.text} font-bold text-xl hover:bg-primary-500 hover:text-gray-900 transition-colors`}
                  >
                    {key === "clear" ? "C" : key}
                  </button>
                ))}
                {["1", "2", "3", "."].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleNumpadClick(key)}
                    className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} ${themeClasses.text} font-bold text-xl hover:bg-primary-500 hover:text-gray-900 transition-colors`}
                  >
                    {key}
                  </button>
                ))}
                <button
                  onClick={() => handleNumpadClick("0")}
                  className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} ${themeClasses.text} font-bold text-xl hover:bg-primary-500 hover:text-gray-900 transition-colors col-span-2`}
                >
                  0
                </button>
                <button
                  onClick={handleAddItem}
                  disabled={!currentPrice || parseFloat(currentPrice) <= 0}
                  className="p-4 rounded-xl bg-primary-500 text-gray-900 font-bold text-xl disabled:opacity-50 disabled:cursor-not-allowed col-span-2"
                >
                  <i className="ri-add-line mr-1"></i>
                  {t("posQuickSale.add")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`w-80 lg:w-96 ${themeClasses.bg} border-l ${themeClasses.border} flex flex-col`}>
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posQuickSale.saleItems")}</h2>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {items.length} {t("posTerminal.items")}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <i className={`ri-flashlight-line text-4xl ${themeClasses.textSecondary} mb-2`}></i>
              <p className={themeClasses.textSecondary}>{t("posQuickSale.noItems")}</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className={`${themeClasses.cardBg} rounded-xl p-3 border ${themeClasses.border}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className={`font-medium ${themeClasses.text}`}>{item.name}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      ${item.price.toFixed(2)} × {item.quantity}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${themeClasses.text}`}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-600 p-1"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className={`p-4 border-t ${themeClasses.border} space-y-3`}>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.subtotal")}</span>
            <span className={themeClasses.text}>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.tax")} (16%)</span>
            <span className={themeClasses.text}>${tax.toFixed(2)}</span>
          </div>
          <div className={`flex justify-between text-lg font-bold border-t ${themeClasses.border} pt-3`}>
            <span className={themeClasses.text}>{t("posTerminal.total")}</span>
            <span className="text-primary-500">${total.toFixed(2)}</span>
          </div>

          <button
            disabled={items.length === 0}
            className="w-full p-4 rounded-xl bg-primary-500 text-gray-900 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-wallet-3-line mr-2"></i>
            {t("posTerminal.checkout")}
          </button>
        </div>
      </div>
    </div>
  );
}
